import type { CustomSeriesRenderItemAPI, CustomSeriesRenderItemParams, CustomSeriesRenderItemReturn } from "echarts";
import type { CustomSeriesOption } from "echarts/charts";
import { graphic } from "echarts/core";
import type { TimeEvent } from "../../band-data/types";
import { PLOT_X_AXIS_ID } from "../layout/plot-geometry";
import { ACTUAL_HIGHLIGHT, MIN_BAR_PX, SCHEDULED_HIGHLIGHT } from "./band-geometry";
import { toBandRows } from "./build-band-options";

type HighlightRole = "scheduled" | "actual";
type PixelRect = { x: number; y: number; width: number; height: number };
type Span = [startMs: number, endMs: number];
/** whole is 0 for a piece of a scheduled span left after cutting the valve runs out of it. */
export type HighlightRow = [startMs: number, endMs: number, whole: 0 | 1];

export function renderHighlightRegion(
    role: HighlightRole,
    params: CustomSeriesRenderItemParams,
    api: CustomSeriesRenderItemAPI,
): CustomSeriesRenderItemReturn {
    const [startX = 0] = api.coord([Number(api.value(0)), 0]);
    const [endX = 0] = api.coord([Number(api.value(1)), 0]);
    // As wide as the bar it sits over; a piece cut by the valve keeps its own width.
    const minWidthPx = Number(api.value(2)) === 1 ? MIN_BAR_PX : 0;

    const coordSys = params.coordSys as CustomSeriesRenderItemParams["coordSys"] & PixelRect;
    const clipped = graphic.clipRectByRect(
        { x: startX, y: coordSys.y, width: Math.max(endX - startX, minWidthPx), height: coordSys.height },
        { x: coordSys.x, y: coordSys.y, width: coordSys.width, height: coordSys.height },
    );
    if (!clipped || clipped.width <= 0) return undefined;

    return {
        type: "rect",
        silent: true,
        shape: clipped,
        style: { fill: role === "scheduled" ? SCHEDULED_HIGHLIGHT : ACTUAL_HIGHLIGHT },
    };
}

function cut([startMs, endMs]: Span, [cutStartMs, cutEndMs]: Span): Span[] {
    if (cutEndMs <= startMs || cutStartMs >= endMs) return [[startMs, endMs]];
    const kept: Span[] = [];
    if (cutStartMs > startMs) kept.push([startMs, cutStartMs]);
    if (cutEndMs < endMs) kept.push([cutEndMs, endMs]);
    return kept;
}

/** The parts of each scheduled span no valve-open span covers, so the two tints never stack. */
export function withoutActual(scheduled: Span[], actual: Span[]): HighlightRow[] {
    return scheduled.flatMap(([startMs, endMs]) =>
        actual
            .reduce<Span[]>(
                (pieces, [cutStartMs, cutEndMs]) => pieces.flatMap((piece) => cut(piece, [cutStartMs, cutEndMs])),
                [[startMs, endMs]],
            )
            .map(
                ([pieceStartMs, pieceEndMs]): HighlightRow => [
                    pieceStartMs,
                    pieceEndMs,
                    pieceStartMs === startMs && pieceEndMs === endMs ? 1 : 0,
                ],
            ),
    );
}

const toSpans = (events: TimeEvent[], window: { startMs: number; endMs: number }, nowMs: number): Span[] =>
    toBandRows(events, window, nowMs).map(([startMs, endMs]) => [startMs, endMs]);

export function buildHighlightSeries(
    scheduled: TimeEvent[],
    actual: TimeEvent[],
    window: { startMs: number; endMs: number },
    nowMs: number,
    plotYAxisId: string,
): CustomSeriesOption[] {
    const actualSpans = toSpans(actual, window, nowMs);
    const series = (role: HighlightRole, data: HighlightRow[]): CustomSeriesOption => ({
        id: `highlight-${role}`,
        type: "custom",
        xAxisId: PLOT_X_AXIS_ID,
        yAxisId: plotYAxisId,
        silent: true,
        tooltip: { show: false },
        // Under the line and bar series, which sit at the default z of 2.
        z: 1,
        renderItem: (params, api) => renderHighlightRegion(role, params, api),
        data,
    });

    return [
        series("scheduled", withoutActual(toSpans(scheduled, window, nowMs), actualSpans)),
        series(
            "actual",
            actualSpans.map(([startMs, endMs]): HighlightRow => [startMs, endMs, 1]),
        ),
    ];
}
